import { z } from 'zod'
import { SITUACOES_DA_CAMPANHA_NO_PORTAL } from '@dv/domain'
import { dataNominal,email,uuid } from './comum.js'
import { visaoDoPortal } from './portal-admin.js'
import { resultadosDaCampanhaNoPortal } from './campanha-portal.js'

/* Os resultados da campanha no portal (P-46 a P-50, DS-673 a DS-676) moram em `campanha-portal.ts` e saem por aqui. */
export * from './campanha-portal.js'

const decimalMonetario=z.string().regex(/^-?\d{1,16}(?:\.\d{1,2})?$/)

/**
 * Percentual como FRAÇÃO — a forma em que o banco guarda e o `::text` entrega.
 *
 * `dv_pct` é `numeric(9,6)`: 10% é `0.100000`, com SEIS casas, e é assim que sai do
 * `participacao::text` do materializador. O `decimalMonetario` aceita no máximo duas,
 * então tipar percentual com ele recusa todo dado real — e recusa também o NULL, que a
 * coluna permite. A conversão para "10%" acontece na apresentação, por
 * `fracaoParaPercentual`, e em lugar nenhum antes.
 *
 * O regex confere a FORMA, e forma nenhuma distingue 0,10 de 10,000000: a unidade quem
 * fixa é o teste que lê o texto da tela.
 */
const fracaoDePercentual=z.string().regex(/^-?\d{1,3}(?:\.\d{1,6})?$/)

/* ---------------------------------------------------------------------------
 * O contexto externo é o PORTAL (D-131): nome, UMA visão e as empresas que ele mostra. A pessoa
 * pode entrar em vários; dentro de um portal com mais de uma empresa ela vê "Todas" ou escolhe
 * uma. Quem decide o conjunto é o banco (`fn_portal_contexto`), nunca o corpo da requisição.
 * ------------------------------------------------------------------------- */

/** Uma empresa do portal, como a SPA a mostra no seletor e na coluna Empresa. */
export const empresaDoPortalNoExterno=z.object({id:uuid,nome:z.string()})
export type EmpresaDoPortalNoExterno=z.infer<typeof empresaDoPortalNoExterno>

/** Um portal em que a pessoa entra — só os ativos, com membro ativo e ao menos uma empresa. */
export const portalDoExterno=z.object({id:uuid,nome:z.string(),visao:visaoDoPortal,empresas:z.array(empresaDoPortalNoExterno).min(1)})
export type PortalDoExterno=z.infer<typeof portalDoExterno>

/**
 * Quem está do outro lado e onde está. `portalAtualId` null = ainda não entrou num portal (a
 * escolha, ou a entrada automática quando há um só). `empresaAtualId` null com portal = Todas.
 */
export const euExternoDoPortal=z.object({
  nome:z.string(),email,
  portalAtualId:uuid.nullable(),
  empresaAtualId:uuid.nullable(),
  portais:z.array(portalDoExterno),
})
export type EuExternoDoPortal=z.infer<typeof euExternoDoPortal>

/** O corpo do POST /contexto: o portal e, dentro dele, uma empresa — ou `null`, Todas. */
export const entradaDeContextoDoPortal=z.object({portal_id:z.string(),empresa_id:z.string().nullable().default(null)})
export type EntradaDeContextoDoPortal=z.infer<typeof entradaDeContextoDoPortal>

export const resumoDoParceiroNoPortal=z.object({aReceber:decimalMonetario,liberadas:decimalMonetario,aguardandoCliente:decimalMonetario,pedidosProntos:z.string(),pedidosEsperando:z.string(),pedidosNaoFaturados:z.string()})
export type ResumoDoParceiroNoPortal=z.infer<typeof resumoDoParceiroNoPortal>
/**
 * A linha da tabela "Comissões a receber" (11.1) — agora INTEIRA (lote P3).
 *
 * O modelo de leitura já guardava tudo isto desde o materializador; o endpoint
 * selecionava cinco colunas e a tela mostrava quatro. Não é API nova: é consumo do que
 * já estava materializado, e é a diferença entre uma tabela de quatro colunas e as sete
 * que a spec descreve — sem ref externa, sem base × %, sem previsão de pagamento e sem
 * a nota que explica por que a linha está bloqueada.
 */
export const comissaoDoParceiroNoPortal=z.object({
  id:uuid,
  /** A beneficiária da comissão — a empresa do portal que a recebe (coluna Empresa em Todas). */
  empresaId:uuid,
  pedidoCodigo:z.string(),
  refExterna:z.string().nullable(),
  clienteNome:z.string(),
  /** D-74: entre parênteses só quando informado E diferente do cliente. */
  anuncianteNome:z.string().nullable(),
  baseValor:decimalMonetario,
  percentual:z.string().nullable(),
  valorComissao:decimalMonetario,
  statusLinha:z.enum(['Liberada','Aguardando cliente','A faturar','Prevista','Paga']),
  /** A agregação da faixa "o que destrava cada bloco". */
  statusBloco:z.enum(['pronto_para_faturar','esperando_cliente','nao_faturado']),
  /** A frase que explica a situação da linha — vai no botão "Detalhes". */
  nota:z.string().nullable(),
  liberadaVencimento:dataNominal.nullable(),
  previsaoPagamento:dataNominal.nullable(),
  pagamentoData:dataNominal.nullable(),
})
export type ComissaoDoParceiroNoPortal=z.infer<typeof comissaoDoParceiroNoPortal>

/**
 * Uma campanha, UMA vez: em Todas, a mesma campanha aparece para o Cliente e para o Anunciante do
 * pedido, e a API deduplica por `campanhaId` (o investimento não conta duas vezes). `empresaIds`
 * são as empresas do contexto que a alcançam; `contratadaPorOutraEmpresa` = nenhuma delas é o
 * Cliente do pedido (só Anunciante) — o cartão diz isso sem nomear quem contratou.
 *
 * P-11 (D-189, DS-658): `status` fica no vocabulário da versão anterior (a Pausada é 'Veiculando' só nele), e
 * `situacao` diz o que a DigitalView registrou ("Ainda não começou", "Em andamento", "Pausada pela DigitalView",
 * "Concluída pela DigitalView"). Opcional e anulável: a linha materializada antes da coluna vem nula, e a SPA lê
 * `situacao ?? status`. O esquema anterior aceita a resposta nova (o campo a mais some no parse).
 *
 * P-46, P-47 (0200, DS-674): `resultados` são os números da campanha para quem lê. `null` = não publicados para este
 * portal (interruptor do público desligado, pedido cancelado) — o cartão continua; com a publicação autorizada AGORA e
 * a projeção ainda sem a linha do conjunto atual, vem `Em atualização`, sem números. Opcional para a dublê anterior; o
 * servidor sempre manda.
 */
export const situacaoDaCampanhaNoPortal=z.enum(SITUACOES_DA_CAMPANHA_NO_PORTAL)
export const campanhaDoClienteNoPortal=z.object({campanhaId:uuid,codigo:z.string(),refExterna:z.string().nullable(),nome:z.string(),inicio:dataNominal,fim:dataNominal,status:z.enum(['Veiculando','Programada','Concluída']),situacao:situacaoDaCampanhaNoPortal.nullable().optional(),valor:decimalMonetario,nota:z.string().nullable(),empresaIds:z.array(uuid).min(1),contratadaPorOutraEmpresa:z.boolean(),resultados:resultadosDaCampanhaNoPortal.nullable().optional()})
export type CampanhaDoClienteNoPortal=z.infer<typeof campanhaDoClienteNoPortal>
/**
 * A nota que aparece na posição fiscal do cliente (lote P2).
 *
 * `notas` era `z.array(z.unknown())`, e o que estava por trás disso não era preguiça: a
 * tela não usava o campo, então ninguém precisava descrevê-lo. Agora ela usa — número,
 * situação e VENCIMENTO, que é o que pinta a linha de vermelho —, e um `unknown` faria
 * a validação passar por cima justamente do dado que decide a cor.
 */
export const notaDoPedidoNoPortal=z.object({
  id:uuid,
  /** Já vem no formato de exibição ("NF 38") — o materializador monta. */
  numero:z.string(),
  serie:z.string().nullable(),
  situacao:z.string(),
  emissao:dataNominal.nullable(),
  vencimento:dataNominal.nullable(),
})
export type NotaDoPedidoNoPortal=z.infer<typeof notaDoPedidoNoPortal>

/** Saldo NULL: a origem de desconto legado requer conferência (DS496). */
export const pedidoDoClienteNoPortal=z.object({pedidoId:uuid,empresaId:uuid,codigo:z.string(),titulo:z.string(),dataVenda:dataNominal,valor:decimalMonetario,situacao:z.string(),notas:z.array(notaDoPedidoNoPortal),saldoAberto:decimalMonetario.nullable()})
export type PedidoDoClienteNoPortal=z.infer<typeof pedidoDoClienteNoPortal>
/**
 * O id de uma linha de PROJEÇÃO do portal não é um uuid v7. O materializador o deriva de
 * `md5(anexo:empresa)::uuid` (0080, `v_fonte_documento`), e a versão e a variante saem do
 * hash, quaisquer. Validar com o `uuid` das linhas transacionais recusava a lista de
 * Documentos inteira na primeira linha real, e o download recusava três de cada quatro:
 * a HML do lote de 23/09 achou, ao baixar o primeiro relatório de campanha compartilhado.
 * A forma continua conferida (8-4-4-4-12 hex); a posse, quem confere é a RLS.
 */
export const idDaProjecaoDoPortal=z.guid()
/**
 * Um documento, UMA vez: em Todas, o mesmo anexo compartilhado alcança várias empresas do portal e
 * a API deduplica por anexo. `id` é o de UMA das linhas da projeção (serve ao download, que a RLS
 * confere pelo conjunto); `empresaIds` são as empresas do contexto que o recebem.
 */
export const documentoDoPortal=z.object({id:idDaProjecaoDoPortal,categoria:z.enum(['PI','Relatório','NF','Outro']),nome:z.string(),mimeType:z.string(),tamanhoBytes:z.string(),dataDocumento:dataNominal,meta:z.string().nullable(),empresaIds:z.array(uuid).min(1)})
export type DocumentoDoPortal=z.infer<typeof documentoDoPortal>

/* ---------------------------------------------------------------------------
 * Os quatro blocos da 11.1 que a API servia e a tela não consumia (lote P3).
 *
 * Os endpoints existiam desde o passo do materializador; a SPA lia três dos seis campos
 * do resumo e ignorava trajetória, composição, extrato e pipeline. Descrever o contrato
 * é o que permite a tela VALIDAR o que recebe em vez de confiar.
 * ------------------------------------------------------------------------- */
export const trajetoriaDoParceiroNoPortal=z.object({
  competencia:z.string(),
  vendas:decimalMonetario,
  comissao:decimalMonetario,
  pedidos:z.string(),
})
export type TrajetoriaDoParceiroNoPortal=z.infer<typeof trajetoriaDoParceiroNoPortal>

export const fatiaDaComposicaoNoPortal=z.object({nome:z.string(),valor:decimalMonetario})
export const composicaoDoParceiroNoPortal=z.object({
  clientes:z.array(fatiaDaComposicaoNoPortal),
  produtos:z.array(fatiaDaComposicaoNoPortal),
})
export type ComposicaoDoParceiroNoPortal=z.infer<typeof composicaoDoParceiroNoPortal>

export const linhaDoExtratoDoParceiroNoPortal=z.object({
  /** A beneficiária: cada empresa emite a SUA nota, e o extrato agrupa por ela (G11 por parceiro). */
  empresaId:uuid,
  pedidoCodigo:z.string(),
  clienteNome:z.string(),
  baseValor:decimalMonetario,
  /** Comissão fixa é válida no domínio e viaja sem percentual. */
  percentual:fracaoDePercentual.nullable(),
  liberadaValor:decimalMonetario,
  liberadaVencimento:dataNominal,
  /** Contra quem o parceiro emite a nota dele — a pergunta que o extrato responde. */
  contraQuemEmitir:z.string(),
})
export type LinhaDoExtratoDoParceiroNoPortal=z.infer<typeof linhaDoExtratoDoParceiroNoPortal>

export const negocioDoPipelineNoPortal=z.object({
  /** As empresas do contexto que participam do negócio; em Todas o negócio aparece UMA vez. */
  empresaIds:z.array(uuid).min(1),
  negocioCodigo:z.string(),
  nome:z.string(),
  estagio:z.string(),
  valor:decimalMonetario,
  /**
   * Fração (`dv_pct`), e opcional: o negócio pode não ter participação atribuída. Em Todas, com
   * duas empresas do portal no mesmo negócio, é a SOMA das participações delas.
   */
  participacao:fracaoDePercentual.nullable(),
})
export type NegocioDoPipelineNoPortal=z.infer<typeof negocioDoPipelineNoPortal>

export const FILA_MATERIALIZAR_PORTAL='portal.materializar'
/** A rodada do portal (0200): as seis projeções de sempre, os `resultados` da campanha (P-47) e os cartões do Parceiro (P-48). */
export const resultadoDaMaterializacaoDoPortal=z.object({
  comissoes:z.number().int().nonnegative(),produtos:z.number().int().nonnegative(),
  pipeline:z.number().int().nonnegative(),pedidos:z.number().int().nonnegative(),
  campanhas:z.number().int().nonnegative(),documentos:z.number().int().nonnegative(),
  resultados:z.number().int().nonnegative(),cartoesDoParceiro:z.number().int().nonnegative(),
})
export type ResultadoDaMaterializacaoDoPortal=z.infer<typeof resultadoDaMaterializacaoDoPortal>
